import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, User, LogOut, Clock, Award, Briefcase, Lock, HelpCircle, ChevronDown, BookOpen, Info } from 'lucide-react';
import api from '../services/api';

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [showHelpMenu, setShowHelpMenu] = useState(false);
    const [config, setConfig] = useState({ orgLogoUrl: '', orgName: '' });
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.name || 'User';

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await api.get('/auth/config');
            setConfig(response.data);
        } catch (err) {
            console.error('Failed to fetch navbar config', err);
        }
    };

    const isActive = (path) => location.pathname === path;

    const navItems = [
        { path: '/dashboard', icon: Home, label: 'Home' },
    ];

    const handleLogout = () => {
        localStorage.removeItem('isAuthenticated');
        window.location.href = '/';
    };

    return (
        <nav style={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid var(--border)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            padding: '0.75rem 0'
        }}>
            <div className="container" style={{ padding: '0 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Link to="/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <img src={config.orgLogoUrl || "/logo.png"} alt={config.orgName} style={{ height: '60px', objectFit: 'contain' }} />
                </Link>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '0.5rem',
                                    textDecoration: 'none',
                                    color: isActive(item.path) ? 'var(--primary)' : 'var(--text-muted)',
                                    background: isActive(item.path) ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                                    fontWeight: isActive(item.path) ? 600 : 500,
                                    transition: 'all 0.2s ease',
                                    fontSize: '0.9rem',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <item.icon size={16} />
                                {item.label}
                            </Link>
                        ))}

                        {/* Help Dropdown */}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setShowHelpMenu(!showHelpMenu)}
                                onBlur={() => setTimeout(() => setShowHelpMenu(false), 200)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 1.1rem',
                                    borderRadius: '0.5rem',
                                    border: 'none',
                                    background: showHelpMenu ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                                    color: showHelpMenu ? 'var(--primary)' : 'var(--text-muted)',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <HelpCircle size={16} /> Help <ChevronDown size={14} />
                            </button>

                            {showHelpMenu && (
                                <div style={{
                                    position: 'absolute',
                                    top: '120%',
                                    left: 0,
                                    background: 'white',
                                    border: '1px solid var(--border, #e5e7eb)',
                                    borderRadius: '0.75rem',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                    padding: '0.5rem',
                                    minWidth: '200px',
                                    zIndex: 1000
                                }}>
                                    <Link
                                        to="/help"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            padding: '0.6rem 0.75rem',
                                            textDecoration: 'none',
                                            color: '#374151',
                                            fontSize: '0.85rem',
                                            borderRadius: '0.5rem'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <BookOpen size={16} color="#6366f1" /> Features Help
                                    </Link>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.6rem 0.75rem',
                                        color: '#374151',
                                        fontSize: '0.85rem',
                                        borderRadius: '0.5rem',
                                        cursor: 'default'
                                    }}
                                        title="Version 1.1.0 (Lapse Attendance, Multi-session, Dynamic Branding)"
                                    >
                                        <Info size={16} color="#059669" /> About Tool (v1.1.0)
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            color: 'var(--text-primary, #1f2937)',
                            whiteSpace: 'nowrap'
                        }}>
                            {userName}
                        </span>
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => setShowProfileMenu(!showProfileMenu)}
                                style={{
                                    background: 'var(--bg-secondary, #f3f4f6)',
                                    border: '1px solid var(--border, #e5e7eb)',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary, #4b5563)',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <User size={20} />
                            </button>

                            {showProfileMenu && (
                                <div style={{
                                    position: 'absolute',
                                    top: '120%',
                                    right: 0,
                                    background: 'white',
                                    border: '1px solid var(--border, #e5e7eb)',
                                    borderRadius: '0.75rem',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                    padding: '0.5rem',
                                    minWidth: '150px',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <button
                                        onClick={handleLogout}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            width: '100%',
                                            padding: '0.5rem 0.75rem',
                                            border: 'none',
                                            background: 'transparent',
                                            color: '#ef4444',
                                            borderRadius: '0.5rem',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            fontWeight: 500
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = '#fef2f2'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <LogOut size={16} />
                                        Logout
                                    </button>
                                    <hr style={{ margin: '0.4rem 0', border: 'none', borderTop: '1px solid #eee' }} />
                                    <button
                                        onClick={() => { navigate('/profile'); setShowProfileMenu(false); }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            width: '100%',
                                            padding: '0.5rem 0.75rem',
                                            border: 'none',
                                            background: 'transparent',
                                            color: 'var(--text-primary)',
                                            borderRadius: '0.5rem',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            fontWeight: 500
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Lock size={16} />
                                        My Profile
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
